import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3x3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const TYPE_CONFIG = {
  NATIONAL:  { className: 'bg-blue-500' },
  REGIONAL:  { className: 'bg-purple-500' },
  RELIGIOUS: { className: 'bg-green-500' },
  COMPANY:   { className: 'bg-orange-500' },
  OPTIONAL:  { className: 'bg-gray-500' },
};

const getTypeBadge = (type, t) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.NATIONAL;
  return <Badge className={config.className}>{t(`holidayCommon.types.${type}`, { defaultValue: type })}</Badge>;
};

const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const CalendrierDetailCalendarView = ({ calendar, holidays }) => {
  const { t } = useTranslation();

  if (!calendar || holidays.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Grid3x3 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-foreground">{t("holidayCalendarComponents.calendarView.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("holidayCalendarComponents.calendarView.emptyDesc")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = new Date(holiday.date).getMonth();
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Object.keys(holidaysByMonth)
        .sort((a, b) => a - b)
        .map((month) => (
          <Card key={month}>
            <CardHeader>
              <CardTitle className="text-base">{t(`common.months.${MONTH_NAMES[month]}`)} {calendar.year}</CardTitle>
              <CardDescription>{t("myHolidaysComponents.monthCount", { count: holidaysByMonth[month].length })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[...holidaysByMonth[month]]
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((holiday) => (
                    <div key={holiday.id} className="flex items-start gap-2 p-2 bg-muted/50 rounded">
                      <div className="flex-shrink-0 w-10 h-10 bg-muted rounded flex items-center justify-center">
                        <span className="text-sm font-bold text-foreground">
                          {new Date(holiday.date).getDate()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{holiday.name}</p>
                        <div className="mt-1">{getTypeBadge(holiday.type, t)}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
};

export default CalendrierDetailCalendarView;
